import type { PropsWithChildren, ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { Button } from './Button'

interface ModalProps {
  open: boolean
  title: string
  onClose: () => void
  footer?: ReactNode
  className?: string
}

/**
 * Minimal modal. Not a full Radix dialog — Briefer's surface area is small
 * enough that a click-outside + escape-key handler covers the cases that
 * matter. Promote to @radix-ui/react-dialog if/when nesting or
 * portal-target rules become an issue.
 */
export function Modal({
  open,
  title,
  onClose,
  footer,
  className,
  children,
}: PropsWithChildren<ModalProps>) {
  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
    >
      <div
        className={cn(
          'w-full max-w-lg rounded-xl border border-warm-mist bg-white shadow-lg',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-warm-mist px-6 py-4">
          <h2 className="text-lg font-semibold text-text-dark">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            ×
          </Button>
        </div>
        <div className="px-6 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-warm-mist px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
